import { StateCreator } from 'zustand';
import {
  IFormBuilderTemplateSlice,
  IFormBuilderTemplateFields,
  TDocumentManagementStore,
} from './types';
import { formBuilderApi } from '@/API';
import { convertKeys } from '@/utils/common';

const initState: IFormBuilderTemplateFields = {
  loadingFormBuilderTemplate: false,
  draftFormBuilderTemplate: null,
  templates: [],
};

export const formBuilderTemplatesSlice: StateCreator<
  TDocumentManagementStore,
  [],
  [],
  IFormBuilderTemplateSlice
> = (set, get) => ({
  ...initState,

  setDraftFormBuilderTemplate: (draftFormBuilderTemplate) =>
    set({ draftFormBuilderTemplate }),

  getFormBuilderTemplate: (id) => {
    return get().templates.find((t) => t.id === id) ?? null;
  },

  fetchFormTemplates: async (institutionId) => {
    set({ loadingFormBuilderTemplate: true });

    try {
      const response = await formBuilderApi.getFormTemplates(institutionId);
      const templates = response.data.data.map((item) => convertKeys(item));
      set({ templates });
    } catch (error) {
      console.error('Fetching form templates failed:', error);
      throw error;
    } finally {
      set({ loadingFormBuilderTemplate: false });
    }
  },

  createFormBuilderTemplate: async (data) => {
    try {
      const response = await formBuilderApi.createFormTemplate(
        convertKeys(data, 'snake'),
      );
      const newTemplate = convertKeys(response.data.data);
      set((pr) => ({ templates: [...pr.templates, newTemplate] }));
    } catch (error) {
      console.error('Creating form template failed:', error);
      throw error;
    }
  },

  updateFormBuilderTemplate: async (body) => {
    set({ loadingFormBuilderTemplate: true });
    try {
      const response = await formBuilderApi.updateFormTemplate(
        convertKeys(body, 'snake'),
      );
      const updatedTemplate = convertKeys(response.data.data);
      set((pr) => ({
        templates: pr.templates.map((t) =>
          t.id === updatedTemplate.id ? updatedTemplate : t,
        ),
      }));
    } catch (error) {
      console.error('Updating form template failed:', error);
      throw error;
    } finally {
      set({ loadingFormBuilderTemplate: false });
    }
  },

  deleteFormBuilderTemplate: async (templateId) => {
    set({ loadingFormBuilderTemplate: true });
    try {
      await formBuilderApi.deleteFormTemplate(templateId);

      set((state) => {
        const updatedPackets = state.packets.map((packet) => ({
          ...packet,
          formIds: packet.formIds.filter((formId) => formId !== templateId),
        }));
        return {
          templates: state.templates.filter((t) => t.id !== templateId),
          packets: updatedPackets,
        };
      });
    } catch (error) {
      console.error('Deleting form template failed:', error);
      throw error;
    } finally {
      set({ loadingFormBuilderTemplate: false });
    }
  },

  clearFormBuilderTemplate: () => set(initState),
});
